@if (post) {
  <!-- Hero Section with Cover Image -->
  <article>
    <header class="relative">
      <!-- Cover Image -->
      <div class="h-[50vh] md:h-[60vh] relative">
        <img [src]="post.image" [alt]="post.title" class="w-full h-full object-cover">
        <div class="absolute inset-0 bg-gradient-to-t from-gray-900 via-gray-900/50 to-transparent"></div>
      </div>

      <!-- Title Overlay -->
      <div class="absolute bottom-0 left-0 right-0 pb-12 pt-24">
        <div class="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav class="text-sm text-white/70 mb-4">
            <a routerLink="/" class="hover:text-white">Home</a>
            <span class="mx-2">/</span>
            <a routerLink="/blog" class="hover:text-white">Blog</a>
            <span class="mx-2">/</span>
            <span class="text-white">{{ post.category }}</span>
          </nav>
          <span class="inline-block bg-primary-600 text-white text-sm font-semibold px-4 py-1.5 rounded-full mb-4">
            {{ post.category }}
          </span>
          <h1 class="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6 leading-tight">
            {{ post.title }}
          </h1>
          <div class="flex flex-wrap items-center gap-6 text-white/80">
            <div class="flex items-center gap-3">
              <div class="w-12 h-12 bg-gradient-to-br from-primary-500 to-primary-700 rounded-full flex items-center justify-center text-white font-bold">
                {{ getInitials(post.author) }}
              </div>
              <div>
                <p class="font-semibold text-white">{{ post.author }}</p>
                <p class="text-sm text-white/60">Author</p>
              </div>
            </div>
            <div class="flex items-center gap-2">
              <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/>
              </svg>
              <span>{{ post.date }}</span>
            </div>
            <div class="flex items-center gap-2">
              <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
              </svg>
              <span>{{ post.readTime }}</span>
            </div>
          </div>
        </div>
      </div>
    </header>

    <!-- Article Content - Magazine Style -->
    <div class="bg-white py-16">
      <div class="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <!-- Lead paragraph styling -->
        <div class="prose prose-lg prose-primary max-w-none
          prose-headings:font-bold prose-headings:text-gray-900
          prose-h2:text-2xl prose-h2:mt-12 prose-h2:mb-6 prose-h2:border-b prose-h2:border-gray-200 prose-h2:pb-4
          prose-p:text-gray-700 prose-p:leading-relaxed prose-p:text-lg
          prose-a:text-primary-600 prose-a:no-underline hover:prose-a:underline
          prose-blockquote:border-l-4 prose-blockquote:border-primary-500 prose-blockquote:bg-primary-50 prose-blockquote:py-4 prose-blockquote:px-6 prose-blockquote:italic prose-blockquote:text-gray-700 prose-blockquote:rounded-r-lg
          prose-ul:space-y-2 prose-ol:space-y-2
          prose-li:text-gray-700
          prose-strong:text-gray-900
          [&_.lead]:text-xl [&_.lead]:text-gray-600 [&_.lead]:font-normal [&_.lead]:leading-relaxed [&_.lead]:mb-8 [&_.lead]:first-letter:text-6xl [&_.lead]:first-letter:font-bold [&_.lead]:first-letter:text-primary-600 [&_.lead]:first-letter:float-left [&_.lead]:first-letter:mr-3 [&_.lead]:first-letter:mt-1"
          [innerHTML]="post.content">
        </div>

        <!-- Share Section -->
        <div class="mt-16 pt-8 border-t border-gray-200">
          <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h3 class="font-semibold text-gray-900 mb-2">Share this article</h3>
              <div class="flex gap-3">
                <a href="#" class="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center text-white hover:bg-blue-700 transition-colors">
                  <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                  </svg>
                </a>
                <a href="#" class="w-10 h-10 bg-sky-500 rounded-full flex items-center justify-center text-white hover:bg-sky-600 transition-colors">
                  <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                  </svg>
                </a>
                <a href="#" class="w-10 h-10 bg-blue-700 rounded-full flex items-center justify-center text-white hover:bg-blue-800 transition-colors">
                  <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/>
                  </svg>
                </a>
              </div>
            </div>
            <a routerLink="/blog" class="btn-secondary">
              ← Back to Blog
            </a>
          </div>
        </div>
      </div>
    </div>

    <!-- Related Articles -->
    @if (relatedPosts.length > 0) {
      <section class="py-16 bg-gray-50">
        <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 class="text-2xl md:text-3xl font-bold text-gray-900 mb-8 text-center">Related Articles</h2>
          <div class="grid md:grid-cols-3 gap-8">
            @for (related of relatedPosts; track related.slug) {
              <article class="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-xl hover:-translate-y-1 transition-all duration-300 group">
                <div class="h-48 overflow-hidden">
                  <img [src]="related.image" [alt]="related.title" class="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300">
                </div>
                <div class="p-6">
                  <span class="text-xs font-semibold text-primary-600 bg-primary-50 px-3 py-1 rounded-full">
                    {{ related.category }}
                  </span>
                  <h3 class="text-lg font-bold text-gray-900 mt-3 mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors">
                    {{ related.title }}
                  </h3>
                  <p class="text-gray-600 text-sm mb-4 line-clamp-2">{{ related.excerpt }}</p>
                  <a [routerLink]="['/blog', related.slug]" class="text-primary-600 font-semibold text-sm flex items-center gap-1 hover:gap-2 transition-all">
                    Read Article
                    <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
                    </svg>
                  </a>
                </div>
              </article>
            }
          </div>
        </div>
      </section>
    }

    <!-- CTA Section -->
    <section class="py-16 bg-gradient-to-br from-primary-600 via-primary-700 to-primary-800">
      <div class="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
        <h2 class="text-2xl md:text-3xl font-bold mb-4">Need Help With {{ post.category }}?</h2>
        <p class="text-lg text-white/90 mb-8">
          Our experts are ready to help you implement these strategies for your business.
        </p>
        <a routerLink="/contact" class="inline-block bg-white text-primary-600 px-8 py-4 rounded-lg font-semibold hover:bg-primary-50 transition-colors">
          Get in Touch
        </a>
      </div>
    </section>
  </article>
}
